import { useCallback, useEffect, useRef, useState } from 'react';
import type { Section } from '../components/TopNav';
import { HIDDEN_SETUP_PAGES, type SetupPage } from '../components/setup/SetupNav';

/**
 * The address bar as application state.
 *
 * Which screen you are on, and which order or settings page is open:
 *
 *   /home
 *   /orders
 *   /tracking
 *   /simulations
 *   /orders/WO-6211
 *   /settings
 *   /settings/vehicles
 *   /billing
 *   /account            people
 *   /account/api-keys
 *   /account/profile
 *   /admin
 *
 * Territory and date are deliberately *not* here. An operation runs one
 * territory and a dispatcher works today; carrying both in every URL bought
 * nothing and cost readability, which is the whole point of having URLs at all.
 * They are ordinary application state, and the territory is remembered across
 * reloads. The consequence, accepted knowingly: a link to an order assumes
 * whatever day the recipient is looking at, and the orders screen falls back to
 * its first row when the order named is not on that day.
 *
 * An order is named by the reference the operator's own systems use rather than
 * by our id, so a link reads /orders/WO-6211 and can be recognised in a message.
 *
 * Hand-rolled rather than a router, for the same reason the select and the date
 * picker are ours: the whole surface is three screens and a settings tree.
 *
 * Drawers are not routes. A route, a new order: they sit over whatever is
 * behind them, and putting them in the path would claim they are a place.
 */

export interface Route {
  section: Section;
  setupPage: SetupPage;
  /** The order open on the orders screen, by reference rather than by id. */
  orderRef: string | null;
  /** Which page of Users and API keys. */
  accountTab: AccountTab;
}

export type AccountTab = 'people' | 'api-keys' | 'profile';
const ACCOUNT_TABS: AccountTab[] = ['people', 'api-keys', 'profile'];

const SECTION_PATH: Record<Section, string> = {
  home: 'home',
  orders: 'orders',
  tracking: 'tracking',
  simulations: 'simulations',
  fleet: 'settings',
  billing: 'billing',
  account: 'account',
  audit: 'audit',
};

const SETUP_PAGES: SetupPage[] = [
  'depots',
  'vehicles',
  'drivers',
  'assignment',
  'tags',
  'regions',
  'road-editor',
  'traffic',
  'preferences',
  'tracking-page',
  'notifications',
  'voice',
  'mobile-app',
  'recurrences',
  'webhooks',
  'territory',
];

export function parseRoute(location: { pathname: string }): Route {
  const [head = '', tail = ''] = location.pathname.split('/').filter(Boolean);

  const section: Section =
    head === 'orders'
      ? 'orders'
      : head === 'tracking'
        ? 'tracking'
        : head === 'simulations'
          ? 'simulations'
          : head === 'settings'
            ? 'fleet'
            : head === 'billing'
              ? 'billing'
              : head === 'account'
                ? 'account'
              : head === 'audit'
                ? 'audit'
                : 'home';

  return {
    section,
    setupPage:
      SETUP_PAGES.includes(tail as SetupPage) && !HIDDEN_SETUP_PAGES.includes(tail as SetupPage)
        ? (tail as SetupPage)
        : 'depots',
    orderRef: section === 'orders' && tail ? decodeURIComponent(tail) : null,
    accountTab: section === 'account' && ACCOUNT_TABS.includes(tail as AccountTab) ? (tail as AccountTab) : 'people',
  };
}

export function formatRoute(route: Route): string {
  const head = SECTION_PATH[route.section];
  let path = `/${head}`;

  if (route.section === 'orders' && route.orderRef) {
    path += `/${encodeURIComponent(route.orderRef)}`;
  }
  // The default settings page stays implicit, so /settings is a valid link.
  if (route.section === 'fleet' && route.setupPage !== 'depots') path += `/${route.setupPage}`;
  if (route.section === 'account' && route.accountTab !== 'people') path += `/${route.accountTab}`;

  return path;
}

export interface NavigateOptions {
  /**
   * Rewrite the current entry instead of adding one. For state the application
   * derived rather than the user chose -- falling back to the first order --
   * so Back does not walk through decisions nobody made.
   */
  replace?: boolean;
}

export function useRoute(): [Route, (patch: Partial<Route>, options?: NavigateOptions) => void] {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location));

  /* Read through a ref so navigate never has to be rebuilt as the route moves. */
  const current = useRef(route);
  current.current = route;

  useEffect(() => {
    const onPopState = () => setRoute(parseRoute(window.location));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const navigate = useCallback((patch: Partial<Route>, options: NavigateOptions = {}) => {
    const next = { ...current.current, ...patch };
    const url = formatRoute(next);
    /*
     * Written outside the state updater on purpose. React invokes updaters more
     * than once in development, and a history push from inside one lands twice.
     */
    if (url !== window.location.pathname) {
      if (options.replace) window.history.replaceState(null, '', url);
      else window.history.pushState(null, '', url);
    }
    current.current = next;
    setRoute(next);
  }, []);

  return [route, navigate];
}
